import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ErrorHandler from './ErrorHandler';

interface URLErrorDetectorProps {
  children: React.ReactNode;
}

const URLErrorDetector: React.FC<URLErrorDetectorProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [errorCode, setErrorCode] = useState<number | null>(null);

  useEffect(() => {
    // Prüfe auf Error-Code in URL-Parametern
    const searchParams = new URLSearchParams(location.search);
    const errorParam = searchParams.get('error');
    
    if (errorParam) {
      const code = parseInt(errorParam, 10);
      if (!isNaN(code) && code >= 400 && code < 600) {
        setErrorCode(code);
        // Bereinige die URL nach dem Erkennen des Fehlers
        searchParams.delete('error');
        const newSearch = searchParams.toString();
        const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${location.hash}`;
        navigate(newUrl, { replace: true });
        return;
      }
    }

    // Prüfe auf Error-Code im Hash (für Legacy-Support)
    const hash = location.hash;
    if (hash.startsWith('#error=')) {
      const code = parseInt(hash.replace('#error=', ''), 10);
      if (!isNaN(code) && code >= 400 && code < 600) {
        setErrorCode(code);
        // Bereinige die URL nach dem Erkennen des Fehlers
        navigate(`${location.pathname}${location.search}`, { replace: true });
        return;
      }
    }

    // Prüfe auf Error-Code im sessionStorage (von Apache/index.html gesetzt)
    const storedError = sessionStorage.getItem('apache_error_code');
    if (storedError) {
      const code = parseInt(storedError, 10);
      if (!isNaN(code) && code >= 400 && code < 600) {
        setErrorCode(code);
        // Error-Code nach dem Verwenden entfernen
        sessionStorage.removeItem('apache_error_code');
        return;
      }
    }

    // Kein Error-Code gefunden, normalen Inhalt anzeigen
    setErrorCode(null);
  }, [location, navigate]);

  // Wenn ein Error-Code erkannt wurde, zeige die entsprechende Error-Seite
  if (errorCode) {
    return <ErrorHandler statusCode={errorCode} />;
  }

  // Andernfalls zeige den normalen Inhalt
  return <>{children}</>;
};

export default URLErrorDetector;
